import { useEffect, useState } from 'react';
import {
  type MugshotConfig,
  mugshotAssetUrl,
  RENDER_ORDER,
  type RenderSlot,
  renderSlotFile,
} from '@/shared/lib/mugshot';

export type MugshotPortraitProps = {
  config: MugshotConfig;
  className?: string;
};

/**
 * Flattens `RENDER_ORDER` PNG stack to single image. Pipeline: stamp layers
 * (white pixels overwrite — bakes occlusion), strip near-white to alpha 0,
 * punch halftone dots through remaining ink. Cached per-config signature.
 */
export const MugshotPortrait = ({ config, className }: MugshotPortraitProps) => {
  const url = useCompositeUrl(config);
  if (!url) return <div className={`relative h-full w-full ${className ?? ''}`} />;
  return (
    <div className={`relative h-full w-full ${className ?? ''}`}>
      <img
        src={url}
        alt=""
        aria-hidden
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
      />
    </div>
  );
};

const imgCache = new Map<string, HTMLImageElement>();
const imgInflight = new Map<string, Promise<HTMLImageElement>>();
const compositeCache = new Map<string, string>();
const compositeInflight = new Map<string, Promise<string>>();

/** ≥FULL stripped; (SOFT, FULL) softens for AA edge fade. */
const FULL = 240;
const SOFT = 200;

const decodeImage = (src: string): Promise<HTMLImageElement> => {
  const cached = imgCache.get(src);
  if (cached) return Promise.resolve(cached);
  const existing = imgInflight.get(src);
  if (existing) return existing;
  const promise = (async () => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    await img.decode();
    imgCache.set(src, img);
    imgInflight.delete(src);
    return img;
  })();
  imgInflight.set(src, promise);
  return promise;
};

const lumOf = (px: Uint8ClampedArray, idx4: number): number =>
  Math.max(px[idx4] ?? 0, px[idx4 + 1] ?? 0, px[idx4 + 2] ?? 0);

const applyAlphaForLuminance = (px: Uint8ClampedArray, idx4: number): void => {
  const lum = lumOf(px, idx4);
  if (lum >= FULL) {
    px[idx4 + 3] = 0;
    return;
  }
  if (lum > SOFT) {
    const a = px[idx4 + 3] ?? 255;
    px[idx4 + 3] = Math.round(((FULL - lum) / (FULL - SOFT)) * a);
  }
};

/** Occlusion baked at stamp pass — safe to strip every near-white globally. */
const stripAllWhite = (data: ImageData): void => {
  const px = data.data;
  for (let i = 0; i < px.length; i += 4) {
    applyAlphaForLuminance(px, i);
  }
};

const HALFTONE_TILE = 6;
const HALFTONE_RADIUS = 1;

/** Ben-Day halftone — drop alpha to 0 inside tile-centered dots over ink. */
const punchHalftone = (data: ImageData): void => {
  const { width, height, data: px } = data;
  const r2 = HALFTONE_RADIUS * HALFTONE_RADIUS;
  const half = HALFTONE_TILE / 2;
  for (let y = 0; y < height; y++) {
    const dy = (y % HALFTONE_TILE) - half;
    const dy2 = dy * dy;
    if (dy2 > r2) continue;
    for (let x = 0; x < width; x++) {
      const dx = (x % HALFTONE_TILE) - half;
      if (dx * dx + dy2 > r2) continue;
      const idx = (y * width + x) * 4;
      if ((px[idx + 3] ?? 0) > 0) px[idx + 3] = 0;
    }
  }
};

const signatureOf = (config: MugshotConfig): string =>
  RENDER_ORDER.map((slot: RenderSlot) => {
    if (slot === 'cheveuxBack') return `B:${config.cheveux}`;
    return `${slot}:${config[slot]}`;
  }).join('|');

const flatten = async (config: MugshotConfig): Promise<string> => {
  const layers = RENDER_ORDER.map((slot) => ({
    slot,
    file: renderSlotFile(slot, config),
  })).filter((l): l is { slot: RenderSlot; file: string } => l.file !== null);
  if (layers.length === 0) return '';

  const images = await Promise.all(layers.map(({ file }) => decodeImage(mugshotAssetUrl(file))));
  const first = images[0]!;
  const w = first.naturalWidth;
  const h = first.naturalHeight;

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  for (const img of images) ctx.drawImage(img, 0, 0, w, h);

  const data = ctx.getImageData(0, 0, w, h);
  stripAllWhite(data);
  punchHalftone(data);
  ctx.putImageData(data, 0, 0);

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/png'),
  );
  return blob ? URL.createObjectURL(blob) : '';
};

const composite = (config: MugshotConfig): Promise<string> => {
  const key = signatureOf(config);
  const cached = compositeCache.get(key);
  if (cached) return Promise.resolve(cached);
  const existing = compositeInflight.get(key);
  if (existing) return existing;
  const promise = flatten(config).then((url) => {
    if (url) compositeCache.set(key, url);
    compositeInflight.delete(key);
    return url;
  });
  compositeInflight.set(key, promise);
  return promise;
};

const useCompositeUrl = (config: MugshotConfig): string | null => {
  const key = signatureOf(config);
  const [url, setUrl] = useState<string | null>(() => compositeCache.get(key) ?? null);
  useEffect(() => {
    const cached = compositeCache.get(key);
    if (cached) {
      setUrl(cached);
      return;
    }
    let cancelled = false;
    composite(config)
      .then((next) => {
        if (!cancelled && next) setUrl(next);
      })
      .catch(() => {
        // composite failure leaves prior url — UI degrades gracefully
      });
    return () => {
      cancelled = true;
    };
    // `key` is the real dep; `config` listed to satisfy lint.
  }, [key, config]);
  return url;
};
